import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { X, Send, Bot, Trash2, Plus } from 'lucide-react';
import { Button } from './ui/button';
import DOMPurify from 'dompurify';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WELCOME_MSG = {
  role: 'assistant',
  content: "Hi! I'm your RSO Inventory Expert. I can answer questions about any feature, formula, or workflow — and I can look up live data too.\n\nTry asking:\n- \"What's the reorder point formula?\"\n- \"How many products have zero stock?\"\n- \"Show me top selling SKUs\"\n- \"What does FBA Committed mean?\"",
};

export const Chatbot = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([WELCOME_MSG]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  const startNewSession = () => {
    setMessages([WELCOME_MSG]);
    setSessionId('');
    setInputMessage('');
  };

  const handleSend = async () => {
    if (!inputMessage.trim() || loading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chatbot`, {
        message: userMessage,
        session_id: sessionId,
      });

      if (response.data.session_id && !sessionId) {
        setSessionId(response.data.session_id);
      }

      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: response.data.response },
      ]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClearHistory = async () => {
    try {
      await axios.delete(`${API}/chatbot/history`);
    } catch (error) {
      console.error('Failed to clear history:', error);
    }
    startNewSession();
  };

  const formatMessage = (content) => {
    return content.split('\n').map((line, i) => {
      let formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      formatted = formatted.replace(/`(.*?)`/g, '<code class="bg-muted px-1 py-0.5 rounded text-xs font-mono">$1</code>');
      if (formatted.startsWith('- ') || formatted.startsWith('* ')) {
        formatted = '<span class="ml-2">' + formatted + '</span>';
      }
      const sanitized = DOMPurify.sanitize(formatted, { ALLOWED_TAGS: ['strong', 'code', 'span'], ALLOWED_ATTR: ['class'] });
      return (
        <span key={`line-${i}`}>
          <span dangerouslySetInnerHTML={{ __html: sanitized }} />
          {i < content.split('\n').length - 1 && <br />}
        </span>
      );
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed bottom-16 right-4 w-[420px] h-[560px] bg-background border-2 border-primary/20 rounded-lg shadow-2xl flex flex-col z-50"
      data-testid="chatbot"
    >
      {/* Header */}
      <div className="bg-primary text-primary-foreground p-3 flex items-center justify-between rounded-t-lg">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5" strokeWidth={1.5} />
          <div>
            <span className="font-bold text-sm">RSO Inventory Expert</span>
            <span className="block text-[10px] opacity-75">Knows your app inside out</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={startNewSession}
            data-testid="new-chat-btn"
            className="hover:bg-primary-foreground/10 rounded p-1.5 transition-colors"
            title="New conversation"
          >
            <Plus className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <button
            onClick={handleClearHistory}
            data-testid="clear-history-btn"
            className="hover:bg-primary-foreground/10 rounded p-1.5 transition-colors"
            title="Clear all history"
          >
            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <button
            onClick={onClose}
            data-testid="close-chatbot"
            className="hover:bg-primary-foreground/10 rounded p-1.5 transition-colors"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chatbot-messages">
        {messages.map((msg, idx) => (
          <div
            key={`msg-${msg.role}-${idx}`}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-lg p-3 ${
                msg.role === 'user'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted/80 text-foreground border border-border/50'
              }`}
            >
              <div className="text-sm leading-relaxed whitespace-pre-wrap">
                {msg.role === 'assistant' ? formatMessage(msg.content) : msg.content}
              </div>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-muted/80 text-foreground rounded-lg p-3 border border-border/50">
              <div className="flex items-center gap-2 text-sm">
                <div className="flex gap-1">
                  <span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                  <span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                </div>
                <span className="text-muted-foreground text-xs">Looking up data...</span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t border-border">
        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything about the app..."
            data-testid="chatbot-input"
            disabled={loading}
            rows={1}
            className="flex-1 bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary placeholder:text-muted-foreground disabled:opacity-50"
            style={{ maxHeight: '80px' }}
          />
          <Button
            onClick={handleSend}
            data-testid="chatbot-send"
            disabled={loading || !inputMessage.trim()}
            size="sm"
            className="px-3 h-auto self-end"
          >
            <Send className="w-4 h-4" strokeWidth={1.5} />
          </Button>
        </div>
      </div>
    </div>
  );
};
